/**
 * Where a scoring run stands, in checks (one item × one evaluator): scored,
 * failed, skipped (not applicable), and what is left. Imported items are not
 * scored items: a trace run imports every trace at once, then scores them.
 */

import { formatCount as n } from "@/lib/format";
import type { ExperimentSummary, ScoringProgress, ScoringRun } from "../types";

export interface ScoringTally {
  total: number;
  scored: number;
  failed: number;
  skipped: number;
  /** Checks with no result yet: waiting while the run is live, never scored once it ended. */
  pending: number;
}

export function scoringTally(run: ScoringRun): ScoringTally {
  const { total, scored, errors, not_applicable } = run.counts;
  const pending = Math.max(0, total - scored - errors - not_applicable);
  return { total, scored, failed: errors, skipped: not_applicable, pending };
}

export function progressTally(p: ScoringProgress): ScoringTally {
  return { total: p.total, scored: p.completed, failed: p.failed, skipped: p.skipped, pending: p.pending };
}

/** The server's progress for the latest scoring run, when it sends one. */
export function experimentTally(e: ExperimentSummary): ScoringTally | null {
  const progress = e.scoring?.progress;
  return progress ? progressTally(progress) : null;
}

/** The latest pass scored only some items ("Rerun failed"): its numbers are not the run's. */
export function isPartialPass(e: ExperimentSummary): boolean {
  const p = e.scoring?.progress;
  return !!p && p.scope !== "all";
}

/** Every check as it stands, after any "Rerun failed": the server's `full_pass`, else the latest
 *  pass if it covered every item. Null when only a partial pass is known. */
export function overallTally(e: ExperimentSummary): ScoringTally | null {
  const fullPass = e.scoring?.full_pass;
  if (fullPass) return progressTally(fullPass);
  return isPartialPass(e) ? null : experimentTally(e);
}

/** Checks each evaluator owes on a trace run (one per item), when every evaluator covers every
 *  item; null when unknown. Lets one evaluator's counts add up to the Checks line. */
export function checksPerEvaluator(e: ExperimentSummary): number | null {
  if (e.runner !== "dataset_output") return null;
  const pass = e.scoring?.full_pass ?? (isPartialPass(e) ? null : e.scoring?.progress);
  if (!pass || !e.evaluators.length || pass.total !== pass.items * e.evaluators.length) return null;
  return pass.items;
}

/** A "Rerun failed" pass is still scoring on an ended run. A stopped one keeps its unrun checks
 *  as pending too, so ask the run's status, not the count. */
export function isRetrying(e: ExperimentSummary): boolean {
  const p = e.scoring?.progress;
  const open = e.scoring?.latest_run_status === "queued" || e.scoring?.latest_run_status === "running";
  return !!p && p.scope !== "all" && p.pending > 0 && open;
}

/** Checks that reached an end state, for the bar. */
export const finishedChecks = (t: ScoringTally): number => t.scored + t.failed + t.skipped;

/** "70 of 100 scored · 25 pending · 3 failed · 2 skipped"; zeros are left out, except scored. */
export function tallyParts(t: ScoringTally, live: boolean): string[] {
  return [
    `${n(t.scored)} of ${n(t.total)} scored`,
    t.pending > 0 ? `${n(t.pending)} ${live ? "pending" : "not scored"}` : "",
    t.failed > 0 ? `${n(t.failed)} failed` : "",
    t.skipped > 0 ? `${n(t.skipped)} skipped` : "",
  ].filter(Boolean);
}
